import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { 
  RefreshCcw, 
  Plus, 
  Search, 
  User, 
  Calendar,
  Loader2,
  IndianRupee,
  XCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

const SIP_FREQUENCIES = [
  { value: 'MONTHLY', label: 'Monthly', description: 'Every month on selected date' },
  { value: 'QUARTERLY', label: 'Quarterly', description: 'Every 3 months' },
  { value: 'HALF_YEARLY', label: 'Half Yearly', description: 'Every 6 months' },
  { value: 'YEARLY', label: 'Yearly', description: 'Once a year' },
];

const SIP_DATES = Array.from({ length: 28 }, (_, i) => i + 1);

export const SIPManagementPage = () => {
  const [clients, setClients] = useState([]);
  const [sips, setSips] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [funds, setFunds] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showNewSIPDialog, setShowNewSIPDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [sipToCancel, setSipToCancel] = useState(null);
  const [filterClient, setFilterClient] = useState('all');
  
  // SIP form state
  const [sipForm, setSipForm] = useState({
    amount: '',
    frequency: 'MONTHLY',
    sipDate: '1',
    startDate: '',
    installments: '120'
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientsRes, sipsRes] = await Promise.all([
        axios.get(`${API}/bse/clients?status=registered`),
        axios.get(`${API}/bse/sip?active_only=false`)
      ]);
      setClients(clientsRes.data.clients || []);
      setSips(sipsRes.data.sips || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const searchFunds = async (query) => {
    if (!query || query.length < 2) {
      setFunds([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/funds/search?q=${encodeURIComponent(query)}`);
      setFunds(response.data.funds || []);
    } catch (error) {
      console.error('Error searching funds:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleFundSelect = (fund) => {
    setSelectedFund(fund);
    setFunds([]);
    setSearchQuery(fund.schemeName);
  };

  const handleSIPSubmit = async () => {
    if (!selectedClient || !selectedFund) {
      toast.error('Please select client and fund');
      return;
    }
    
    if (parseFloat(sipForm.amount) < 500) {
      toast.error('Minimum SIP amount is ₹500');
      return;
    }
    
    if (!sipForm.startDate) {
      toast.error('Please select start date');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/bse/sip/register`, {
        ucc_code: selectedClient.ucc_code,
        scheme_code: selectedFund.schemeCode,
        amount: parseFloat(sipForm.amount),
        frequency: sipForm.frequency,
        start_date: sipForm.startDate,
        no_of_installments: parseInt(sipForm.installments)
      });
      
      if (response.data.status === 'registered') {
        toast.success(`SIP registered! Reference: ${response.data.bse_sip_ref}`);
      } else {
        toast.success('SIP saved. Registration pending.');
      }
      
      setShowNewSIPDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register SIP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSIP = async () => {
    if (!sipToCancel) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/bse/sip/${sipToCancel.sip_id}/cancel`);
      toast.success('SIP cancelled successfully');
      setShowCancelDialog(false);
      setSipToCancel(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel SIP');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedClient(null);
    setSelectedFund(null);
    setSearchQuery('');
    setSipForm({
      amount: '',
      frequency: 'MONTHLY',
      sipDate: '1',
      startDate: '',
      installments: '120'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      registered: { className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2 },
      pending: { className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
      cancelled: { className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
      failed: { className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.className} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const filteredSIPs = filterClient === 'all' 
    ? sips 
    : sips.filter(sip => sip.ucc_code === filterClient);

  const activeSIPs = sips.filter(s => s.is_active);
  const totalSIPAmount = activeSIPs.reduce((sum, s) => sum + (s.amount || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="sip-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">SIP Management</h1>
            <p className="text-muted-foreground mt-1">Register and manage systematic investment plans</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCcw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowNewSIPDialog(true)} data-testid="new-sip-btn">
              <Plus className="w-4 h-4 mr-2" />
              New SIP
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active SIPs</p>
                  <p className="text-3xl font-bold text-foreground">{activeSIPs.length}</p>
                </div>
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Monthly SIP</p>
                  <p className="text-3xl font-bold text-foreground">
                    ₹{totalSIPAmount.toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total SIPs</p>
                  <p className="text-3xl font-bold text-foreground">{sips.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <RefreshCcw className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SIP List */}
        <Card className="bg-card/50 backdrop-blur border-border/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">All SIPs</CardTitle>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.ucc_code} value={client.ucc_code}>
                      {client.first_name} {client.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {filteredSIPs.length === 0 ? (
              <div className="text-center py-12">
                <RefreshCcw className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No SIPs found</p>
                <Button className="mt-4" onClick={() => setShowNewSIPDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Register First SIP
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border/50">
                    <tr className="text-left text-sm text-muted-foreground">
                      <th className="p-4 font-medium">SIP ID</th>
                      <th className="p-4 font-medium">Client</th>
                      <th className="p-4 font-medium">Scheme</th>
                      <th className="p-4 font-medium">Amount</th>
                      <th className="p-4 font-medium">Frequency</th>
                      <th className="p-4 font-medium">Start Date</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSIPs.map(sip => {
                      const client = clients.find(c => c.ucc_code === sip.ucc_code);
                      return (
                        <tr key={sip.sip_id} className="border-b border-border/30 hover:bg-muted/20">
                          <td className="p-4 font-mono text-sm">{sip.sip_id}</td>
                          <td className="p-4">
                            <p className="font-medium">{client?.first_name} {client?.last_name}</p>
                            <p className="text-xs text-muted-foreground">{sip.ucc_code}</p>
                          </td>
                          <td className="p-4 text-sm">{sip.scheme_code}</td>
                          <td className="p-4 font-medium">₹{sip.amount?.toLocaleString()}</td>
                          <td className="p-4 text-sm">{sip.frequency}</td>
                          <td className="p-4 text-sm">{sip.start_date}</td>
                          <td className="p-4">{getStatusBadge(sip.bse_status)}</td>
                          <td className="p-4">
                            {sip.is_active && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  setSipToCancel(sip);
                                  setShowCancelDialog(true);
                                }}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Cancel
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* New SIP Dialog */}
        <Dialog open={showNewSIPDialog} onOpenChange={(open) => {
          setShowNewSIPDialog(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Register New SIP</DialogTitle>
              <DialogDescription>Set up a systematic investment plan for your client</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Client Selection */}
              <div>
                <Label>Select Client *</Label>
                <Select 
                  value={selectedClient?.client_id || ''} 
                  onValueChange={(id) => setSelectedClient(clients.find(c => c.client_id === id))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{client.first_name} {client.last_name}</span>
                          <span className="text-muted-foreground">({client.pan})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Fund Search */}
              <div>
                <Label>Select Fund *</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search fund by name..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchFunds(e.target.value);
                    }}
                    className="pl-10"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                  )}
                </div>
                
                {funds.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto border border-border/50 rounded-lg">
                    {funds.map(fund => (
                      <button
                        key={fund.schemeCode}
                        className="w-full text-left p-3 hover:bg-muted/50 border-b border-border/30 last:border-0"
                        onClick={() => handleFundSelect(fund)}
                      >
                        <p className="font-medium text-sm">{fund.schemeName}</p>
                      </button>
                    ))}
                  </div>
                )}
                
                {selectedFund && (
                  <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span className="text-sm">{selectedFund.schemeName}</span>
                  </div>
                )}
              </div>
              
              {/* SIP Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sip-amount">SIP Amount (₹) *</Label>
                  <Input
                    id="sip-amount"
                    type="number"
                    placeholder="5000"
                    value={sipForm.amount}
                    onChange={(e) => setSipForm({...sipForm, amount: e.target.value})}
                    min="500"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum: ₹500</p>
                </div>
                
                <div>
                  <Label>Frequency *</Label>
                  <Select 
                    value={sipForm.frequency} 
                    onValueChange={(v) => setSipForm({...sipForm, frequency: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SIP_FREQUENCIES.map(freq => (
                        <SelectItem key={freq.value} value={freq.value}>
                          {freq.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start-date">Start Date *</Label>
                  <Input
                    id="start-date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={sipForm.startDate}
                    onChange={(e) => setSipForm({...sipForm, startDate: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="installments">Number of Installments</Label>
                  <Input
                    id="installments"
                    type="number"
                    value={sipForm.installments}
                    onChange={(e) => setSipForm({...sipForm, installments: e.target.value})}
                    min="6"
                    max="999"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  SIP will be auto-debited from the client's registered bank account on the selected date every month.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewSIPDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSIPSubmit} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Register SIP
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel SIP Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel SIP</DialogTitle>
              <DialogDescription>Are you sure you want to cancel this SIP?</DialogDescription>
            </DialogHeader>
            
            {sipToCancel && (
              <div className="py-4 space-y-2">
                <p><strong>SIP ID:</strong> {sipToCancel.sip_id}</p>
                <p><strong>Scheme:</strong> {sipToCancel.scheme_code}</p>
                <p><strong>Amount:</strong> ₹{sipToCancel.amount?.toLocaleString()}</p>
                <p><strong>Frequency:</strong> {sipToCancel.frequency}</p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
                Keep SIP
              </Button>
              <Button variant="destructive" onClick={handleCancelSIP} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Yes, Cancel SIP
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SIPManagementPage;
